import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Portal } from './Portal';

interface StatusSelectProps {
  value: string | null;
  onChange: (value: string | null) => Promise<void>;
}

// Map of database values to translation keys
const STATUS_TRANSLATION_MAP: Record<string, string> = {
  'No se ha presentado': 'meetingStatus.notPresented',
  'No nos hemos presentado': 'meetingStatus.weDidNotPresent',
  'Positiva - Seguimiento': 'meetingStatus.positiveFollowUp',
  'Positiva - Cerrado': 'meetingStatus.positiveClosed',
  'Negativa': 'meetingStatus.negative',
  'Negativa - Perfil inadecuado': 'meetingStatus.negativeInadequateProfile',
  'Sin respuesta': 'meetingStatus.noResponse',
  'Cancelada': 'meetingStatus.cancelled'
};

const STATUS_OPTIONS = [
  { value: 'No se ha presentado', color: 'bg-blue-200 text-blue-900' },
  { value: 'No nos hemos presentado', color: 'bg-blue-100 text-blue-800' },
  { value: 'Positiva - Seguimiento', color: 'bg-green-100 text-green-800' },
  { value: 'Positiva - Cerrado', color: 'bg-green-700 text-green-100' },
  { value: 'Negativa', color: 'bg-red-200 text-red-800' },
  { value: 'Negativa - Perfil inadecuado', color: 'bg-red-700 text-red-100' },
  { value: 'Sin respuesta', color: 'bg-gray-300 text-gray-800' },
  { value: 'Cancelada', color: 'bg-gray-800 text-gray-100' }
];

const VIEWPORT_MARGIN = 8;

export const StatusSelect: React.FC<StatusSelectProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; visible: boolean }>({
    top: 0,
    left: 0,
    visible: false,
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentStatus = STATUS_OPTIONS.find(status => status.value === value);

  // Helper function to get translated label for a status value
  const getStatusLabel = (statusValue: string | null): string => {
    if (!statusValue) return t('meetingStatus.selectStatus');
    const translationKey = STATUS_TRANSLATION_MAP[statusValue];
    return translationKey ? t(translationKey) : statusValue;
  };

  const handleStatusChange = async (newStatus: string | null) => {
    setIsOpen(false);
    try {
      await onChange(newStatus);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const updatePosition = () => {
    if (!buttonRef.current || !dropdownRef.current) return;
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current.offsetHeight;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    // Flip above when the dropdown would overflow the viewport and there's more room up top.
    const placeAbove = spaceBelow < dropdownHeight + VIEWPORT_MARGIN && spaceAbove > spaceBelow;
    const top = placeAbove
      ? Math.max(VIEWPORT_MARGIN, buttonRect.top - dropdownHeight - 4)
      : buttonRect.bottom + 4;

    setPosition({ top, left: buttonRect.left, visible: true });
  };

  useLayoutEffect(() => {
    if (isOpen) {
      updatePosition();
    } else {
      setPosition((prev) => ({ ...prev, visible: false }));
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handle = () => updatePosition();
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        dropdownRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`w-full flex items-center justify-between px-2.5 py-1 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-[#0055FF] transition-colors ${
          currentStatus?.color || 'bg-gray-50 text-gray-500'
        }`}
      >
        <span>{getStatusLabel(value)}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <Portal>
          <div
            ref={dropdownRef}
            className="fixed z-[1000] bg-white shadow-lg border border-gray-200 p-2 min-w-[200px]"
            style={{
              top: position.top,
              left: position.left,
              visibility: position.visible ? 'visible' : 'hidden',
            }}
          >
            <div className="flex flex-col gap-1.5">
              <button
                key="clear-status"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusChange(null);
                }}
                className={`text-left px-3 py-1.5 text-xs font-medium transition-colors bg-gray-100 text-gray-700 ${
                  value === null ? 'ring-2 ring-[#0055FF] ring-opacity-50' : ''
                }
                  hover:opacity-90`}
              >
                {t('meetingStatus.clearStatus')}
              </button>
              {STATUS_OPTIONS.map((status) => (
                <button
                  key={status.value}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusChange(status.value);
                  }}
                  className={`text-left px-3 py-1.5 text-xs font-medium transition-colors ${
                    status.color
                  } ${value === status.value ? 'ring-2 ring-[#0055FF] ring-opacity-50' : ''}
                    hover:opacity-90`}
                >
                  {getStatusLabel(status.value)}
                </button>
              ))}
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};